import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import Cookies from "js-cookie";

const LawsuitAmountSelection = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [showSuccessFeeInfo, setShowSuccessFeeInfo] = useState(false);

  // 스크롤 강력 초기화 함수
  const forceScrollTop = () => {
    // 모든 가능한 스크롤 위치를 0으로 설정
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // iOS/Safari 대응을 위한 추가 처리
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      document.body.scrollTo(0, 0);
      document.documentElement.scrollTo(0, 0);
    }

    // ContentWrapper 스크롤 초기화
    const contentWrapper = document.querySelector(".content-wrapper");
    if (contentWrapper) {
      contentWrapper.scrollTop = 0;
    }
  };

  // 컴포넌트 마운트 시 강력하게 스크롤 초기화
  useEffect(() => {
    // 강력한 스크롤 초기화 적용
    forceScrollTop();

    // 컴포넌트 마운트 후 조금 기다렸다가 다시 한번 스크롤 초기화
    setTimeout(() => {
      forceScrollTop();

      const contentWrapper = document.querySelector(".content-wrapper");
      if (contentWrapper) {
        contentWrapper.scrollTop = 0;
      }
    }, 100);

    // 컴포넌트 언마운트 시 정리 함수
    return () => {
      // 정리 작업 수행 (필요 시)
    };
  }, []);

  // 버튼 활성화 여부 체크
  useEffect(() => {
    setIsButtonEnabled(selectedAmount !== null);
  }, [selectedAmount]);

  // 소송 금액 선택 처리를 수정합니다
  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
  };

  // 성공보수 정보 토글
  const toggleSuccessFeeInfo = (e) => {
    e.stopPropagation();
    setShowSuccessFeeInfo(!showSuccessFeeInfo);
  };

  // 다음 페이지로 이동
  const handleNext = () => {
    if (selectedAmount) {
      // ContentWrapper 스크롤 초기화 (이동 전)
      const resetContentWrapperScroll = () => {
        const contentWrapper = document.querySelector(".content-wrapper");
        if (contentWrapper) {
          contentWrapper.scrollTop = 0;
        }
      };

      // 즉시 초기화 시도
      resetContentWrapperScroll();

      // 기본 스크롤 초기화
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // 선택한 금액을 쿠키에 저장
      Cookies.set("lawsuit_amount", selectedAmount.amount, {
        expires: 7,
        path: "/",
      });
      Cookies.set("lawsuit_fee", selectedAmount.fee, { expires: 7, path: "/" });
      Cookies.set(
        "lawsuit_success_fee_percentage",
        selectedAmount.successFeePercentage,
        { expires: 7, path: "/" }
      );

      // 동의서 페이지로 이동
      navigate("/agreement");

      // 이동 후 재시도 (타이밍 문제 해결)
      setTimeout(() => {
        resetContentWrapperScroll();
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 소송 비용 옵션
  const lawsuitOptions = [
    {
      amount: 5000,
      description: "소송비용",
      fee: 5000,
      successFeePercentage: 10,
      recommended: true,
    },
    {
      amount: 2000,
      description: "소송비용",
      fee: 2000,
      successFeePercentage: 12,
      recommended: false,
    },
  ];

  // 컴포넌트 로드 시 이미지 프리로드
  useEffect(() => {
    const preloadImages = () => {
      const activeRadioImg = new Image();
      activeRadioImg.src = "/png/activeRadio.png";

      const inactiveRadioImg = new Image();
      inactiveRadioImg.src = "/png/inactiveRadio.png";
    };

    preloadImages();
  }, []);

  // 라디오 버튼 모양을 커스텀 SVG로 대체
  const RadioButton = ({ isSelected }) => {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {isSelected ? (
          // 선택된 상태 SVG
          <>
            <circle cx="12" cy="12" r="10" fill="#0075FF" />
            <circle cx="12" cy="12" r="6" fill="white" />
          </>
        ) : (
          // 선택되지 않은 상태 SVG
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="#E5E5E5"
            strokeWidth="2"
            fill="none"
          />
        )}
      </svg>
    );
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <TitleSection>
            <Title>소송 비용 선택</Title>
          </TitleSection>

          <SubtitleSection>
            <SubTitle>소송 비용 선택</SubTitle>
            <Description>원하시는 소송 비용 패키지를 선택해주세요</Description>
          </SubtitleSection>

          <DropdownSuccessFeeContainer onClick={toggleSuccessFeeInfo}>
            <DropdownHeader>
              <DropdownHeaderContent>
                <DropdownTitle>성공보수란 무엇인가요?</DropdownTitle>
                <DropdownSubtitle>사건승소시 적용되는 비용</DropdownSubtitle>
              </DropdownHeaderContent>
              <DropdownIcon isOpen={showSuccessFeeInfo}>
                <img src="/png/arrow.png" alt="화살표" />
              </DropdownIcon>
            </DropdownHeader>

            <DropdownContentWrapper isOpen={showSuccessFeeInfo}>
              <DropdownContent>
                <p>
                  위 사건에서 승소(일부 승소 포함)할 경우 "갑"이 승소로 얻은
                  경제적 이익가액(판결금, 조정금, 합의금 포함)을 기준으로 그
                  10%(부가가치세 별도)또는 12%를 성공보수금으로 정하며, "갑"이
                  손해배상금을 수령하는 당일 "을"에게 지급한다.
                </p>
                <p>
                  이 때 '합의금'에는 소송절차의 내외를 불문하고 이루어진 보상
                  협의에 따른 일체의 보상금도 포함되는 것으로 한다. 성공보수금
                  지급이 지체될 경우 연 12%의 지연손해금을 지급한다.
                </p>

                <SuccessFeeTableContainer>
                  <SuccessFeeTableTitle>소송결과</SuccessFeeTableTitle>
                  <SuccessFeeTableRow>
                    <SuccessFeeTableCell>승소</SuccessFeeTableCell>
                    <SuccessFeeTableCell>
                      배상금의 일정 비율
                    </SuccessFeeTableCell>
                  </SuccessFeeTableRow>
                  <SuccessFeeTableRow>
                    <SuccessFeeTableCell>패소</SuccessFeeTableCell>
                    <SuccessFeeTableCell>없음</SuccessFeeTableCell>
                  </SuccessFeeTableRow>
                </SuccessFeeTableContainer>
              </DropdownContent>
            </DropdownContentWrapper>
          </DropdownSuccessFeeContainer>

          <OptionsContainer>
            {lawsuitOptions.map((option, index) => (
              <RadioOption
                key={index}
                onClick={() => handleAmountSelect(option)}
                isSelected={
                  selectedAmount && selectedAmount.amount === option.amount
                }
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleAmountSelect(option);
                  }
                }}
              >
                <RadioLabel>
                  <AmountRow>
                    <AmountText>
                      {option.amount.toLocaleString()}원{" "}
                      <AmountDescription>
                        /{option.description} 등
                      </AmountDescription>
                    </AmountText>
                  </AmountRow>

                  <SuccessFeeTag
                    style={{
                      color: option.amount === 5000 ? "#0075ff" : "#ff6b6b",
                    }}
                  >
                    성공보수 {option.successFeePercentage}%
                  </SuccessFeeTag>
                </RadioLabel>

                <RadioButton
                  isSelected={
                    selectedAmount && selectedAmount.amount === option.amount
                  }
                />
              </RadioOption>
            ))}
          </OptionsContainer>

          <InfoBox>
            <InfoIcon>ⓘ</InfoIcon>
            <InfoText>
              소송비용은 소 제기 시 법원에 예납해야 하는 인지대, 송달료 등
              집단소송의 진행과 관련하여 소요되는 일체의 비용을 포함한
              금액입니다.
            </InfoText>
          </InfoBox>

          <ButtonsContainer>
            <ActionButton
              onClick={handleNext}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={!isButtonEnabled}
            >
              다음으로
            </ActionButton>
          </ButtonsContainer>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f8f8f8;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 30px 20px 40px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const TitleSection = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: 20px;
`;

const SubtitleSection = styled.div`
  width: 100%;
  margin-bottom: 30px;
  color: #333;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  padding-top: 10px;
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  font-size: 22px;
  font-weight: 900;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #2e3840;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #4b5563;
  line-height: 1.4;
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 15px;
  background-color: #eef6ff;
  border-radius: 8px;
  margin: 25px 0;
  width: 100%;
`;

const InfoIcon = styled.span`
  color: #0075ff;
  font-size: 18px;
  margin-right: 10px;
  font-weight: 700;
`;

const InfoText = styled.p`
  color: #444;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  flex: 1;
`;

const OptionsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 10px;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-radius: 8px;
  background-color: ${(props) =>
    props.isSelected ? "rgba(0, 117, 255, 0.1)" : "white"};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${(props) => (props.isSelected ? "#0075FF" : "#e5e5e5")};
  width: 100%;
  box-sizing: border-box;
  position: relative;
  z-index: 1;

  &:hover {
    background-color: ${(props) =>
      props.isSelected ? "rgba(0, 117, 255, 0.1)" : "rgba(0, 117, 255, 0.05)"};
  }

  &:focus,
  &:active {
    outline: 2px solid #0075ff;
  }
`;

const RadioLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const AmountRow = styled.div`
  display: flex;
  align-items: center;
`;

const AmountText = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: baseline;
`;

const AmountDescription = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #666;
  margin-left: 5px;
`;

const SuccessFeeTag = styled.div`
  display: inline-block;
  margin-top: 5px;
  font-size: 14px;
  font-weight: 500;
`;

const DropdownSuccessFeeContainer = styled.div`
  width: 100%;
  margin-bottom: 25px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  background-color: #fff;
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background-color: #fff;
  cursor: pointer;
`;

const DropdownHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const DropdownTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const DropdownSubtitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #666;
`;

const DropdownIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};

  img {
    width: 20px;
    height: 20px;
  }
`;

const DropdownContentWrapper = styled.div`
  max-height: ${(props) => (props.isOpen ? "1000px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  background-color: #f8f9fa;
`;

const DropdownContent = styled.div`
  padding: 15px;
  background-color: #f8f9fa;
  font-size: 12px;
  line-height: 1.6;
  color: #555;

  p {
    margin-bottom: 15px;
    font-size: 12px;
  }
`;

const SuccessFeeTableContainer = styled.div`
  width: 100%;
  margin-top: 15px;
  background-color: #fff;
  border-radius: 0;
  font-size: 12px;
  padding: 15px 20px;
`;

const SuccessFeeTableTitle = styled.div`
  font-weight: 700;
  font-size: 12px;
  margin-bottom: 5px;
`;

const SuccessFeeTableRow = styled.div`
  display: flex;
  font-size: 12px;
  justify-content: space-between;
  margin-bottom: 2px;
`;

const SuccessFeeTableCell = styled.div`
  padding: 4px 0;
  flex: 1;
  font-size: 12px;

  &:first-child {
    font-weight: 600;
  }

  &:last-child {
    text-align: right;
    font-weight: 500;
  }
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
`;

const ActionButton = styled(motion.button)`
  display: flex;
  width: 100%;
  max-width: 400px;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: ${(props) => (props.disabled ? "#E5E5E5" : "#0075ff")};
  color: ${(props) => (props.disabled ? "#999" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  border: none;
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  @media (min-width: 769px) {
    max-width: 430px;
    margin: 0 auto;
  }
`;

export default LawsuitAmountSelection;
